// dashboardUI.js — Sistema de diseño del dashboard ejecutivo de programa.
//
// Un solo juego de componentes con TOKENS por tema (claro / oscuro): ningún
// estilo se escribe dos veces, el ThemeProvider decide los colores. El tema se
// recuerda por navegador (localStorage) y arranca siguiendo la preferencia del
// sistema.
import { useState, useEffect, useCallback } from 'react';
import styled, { createGlobalStyle, useTheme } from 'styled-components';

// ── Tokens ──────────────────────────────────────────────────────────────────
// Semánticos, no literales: `card`, `line`, `muted`… para que el mismo
// componente sirva en los dos temas.
export const THEMES = {
  light: {
    mode: 'light',
    bg: '#f2f6f9',
    card: '#ffffff',
    card2: '#f7fafc',
    line: '#dbe5ec',
    line2: '#eaf0f4',
    text: '#0f2f47',
    text2: '#33546c',
    muted: '#63798a',
    faint: '#8ba0b0',
    headFrom: '#052f49',
    headTo: '#0b5f7c',
    headText: '#ffffff',
    headMuted: '#a9d7e5',
    headSoft: 'rgba(255,255,255,0.14)',
    accent: '#0b5f7c',
    accentSoft: '#e2f1f6',
    input: '#fbfdfe',
    shadow: '0 6px 24px rgba(16,47,67,0.07)',
    ok: '#0f9d76',
    okSoft: 'rgba(15,157,118,0.12)',
    warn: '#c9821b',
    warnSoft: 'rgba(201,130,27,0.13)',
    bad: '#d6503f',
    badSoft: 'rgba(214,80,63,0.12)',
    info: '#1f7fd0',
    lime: '#6f9c1f',
    branchSoft: 'rgba(111,156,31,0.14)',
    track: '#e7eef2',
    serieA: '#0b5f7c',
    serieB: '#0f9d76',
    serieC: '#00a6c7',
  },
  dark: {
    mode: 'dark',
    bg: '#0b1220',
    card: '#11213b',
    card2: '#0f1c33',
    line: '#1e3a5f',
    line2: '#16294a',
    text: '#eaf1fa',
    text2: '#cbd5e1',
    muted: '#93a4bd',
    faint: '#64748b',
    headFrom: '#0a1a2e',
    headTo: '#124063',
    headText: '#ffffff',
    headMuted: '#9fc9dc',
    headSoft: 'rgba(255,255,255,0.09)',
    accent: '#22d3ee',
    accentSoft: 'rgba(34,211,238,0.14)',
    input: '#0b1a30',
    shadow: '0 6px 24px rgba(0,0,0,0.35)',
    ok: '#34d399',
    okSoft: 'rgba(52,211,153,0.16)',
    warn: '#fbbf24',
    warnSoft: 'rgba(251,191,36,0.16)',
    bad: '#f87171',
    badSoft: 'rgba(248,113,113,0.16)',
    info: '#60a5fa',
    lime: '#a3e635',
    branchSoft: 'rgba(163,230,53,0.14)',
    track: '#1e2f4a',
    serieA: '#38bdf8',
    serieB: '#34d399',
    serieC: '#818cf8',
  },
};

const STORAGE_KEY = 'dash_tema';

/** Tema actual + interruptor. Recuerda la elección; si no hay, sigue al sistema. */
export const useTemaDashboard = () => {
  const [modo, setModo] = useState(() => {
    try {
      const g = localStorage.getItem(STORAGE_KEY);
      if (g === 'light' || g === 'dark') return g;
    } catch (_) { /* almacenamiento bloqueado */ }
    return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  });
  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, modo); } catch (_) { /* noop */ } }, [modo]);
  const alternar = useCallback(() => setModo(m => (m === 'dark' ? 'light' : 'dark')), []);
  return [THEMES[modo], modo, alternar];
};

/** Colores para recharts según el tema (ejes, grilla, tooltip, series). */
export const useChartTheme = () => {
  const t = useTheme();
  return {
    grid: t.line2,
    axis: t.muted,
    tooltip: { background: t.card, border: `1px solid ${t.line}`, borderRadius: 10, color: t.text, fontSize: 12 },
    serieA: t.serieA,
    serieB: t.serieB,
    serieC: t.serieC,
  };
};

export const GlobalDash = createGlobalStyle`
  body { background: ${p => p.theme.bg}; }
`;

// ── Formato ─────────────────────────────────────────────────────────────────
export const nf = new Intl.NumberFormat('es-CO');
export const df = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 1 });
export const pct = (x, d = 0) => (Number.isFinite(x)
  ? new Intl.NumberFormat('es-CO', { style: 'percent', maximumFractionDigits: d }).format(x)
  : '—');

/** Verde / ámbar / rojo según el porcentaje (0-100). */
export const tonoPorPct = (t, p) => (p >= 80 ? t.ok : p >= 40 ? t.warn : t.bad);

/** Descarga un CSV con BOM para que Excel respete los acentos. */
export const bajarCSV = (nombre, cols, rows) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [cols.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${String(nombre).replace(/\s+/g, '_')}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 500);
};

// ── Estructura ──────────────────────────────────────────────────────────────
export const Wrap = styled.div`
  min-height:100vh;background:${p => p.theme.bg};color:${p => p.theme.text};
  font-family:system-ui,-apple-system,'Segoe UI',Arial,sans-serif;font-size:16px;line-height:1.45;
`;
export const Top = styled.header`
  min-height:84px;padding:16px clamp(14px,3vw,44px);display:flex;align-items:center;
  justify-content:space-between;gap:18px;flex-wrap:wrap;color:${p => p.theme.headText};
  background:linear-gradient(115deg,${p => p.theme.headFrom},${p => p.theme.headTo});
  position:relative;overflow:hidden;
  &:after{content:"";position:absolute;width:320px;height:320px;border:1px solid rgba(255,255,255,.13);
    border-radius:50%;right:14%;top:-258px;box-shadow:0 0 0 40px rgba(255,255,255,.05),0 0 0 80px rgba(255,255,255,.035);}
`;
export const Brand = styled.div`display:flex;align-items:center;gap:13px;z-index:1;min-width:0;`;
export const BrandName = styled.div`font-weight:800;font-size:clamp(1.05rem,2.2vw,1.4rem);`;
export const BrandSub = styled.div`font-size:0.78rem;color:${p => p.theme.headMuted};`;
export const TopTools = styled.div`display:flex;align-items:center;gap:10px;z-index:1;flex-wrap:wrap;`;
export const CutBox = styled.div`
  padding:8px 13px;border:1px solid rgba(255,255,255,.18);background:${p => p.theme.headSoft};
  border-radius:12px;font-size:0.85rem;font-weight:700;
  small{display:block;font-weight:400;font-size:0.7rem;color:${p => p.theme.headMuted};}
`;
export const ThemeBtn = styled.button`
  height:38px;min-width:38px;padding:0 12px;border-radius:10px;cursor:pointer;font-size:0.85rem;font-weight:600;
  border:1px solid rgba(255,255,255,.22);background:${p => p.theme.headSoft};color:${p => p.theme.headText};
  display:inline-flex;align-items:center;gap:7px;
  &:hover{background:rgba(255,255,255,.2);}
`;
export const Container = styled.main`padding:18px clamp(14px,3vw,44px) 56px;max-width:1800px;margin:0 auto;`;

// ── Filtros ─────────────────────────────────────────────────────────────────
export const FilterBar = styled.section`
  margin:0 clamp(14px,3vw,44px);padding:12px 14px;display:grid;gap:10px;align-items:end;
  grid-template-columns:repeat(auto-fit,minmax(148px,1fr));
  background:${p => p.theme.card};border:1px solid ${p => p.theme.line};border-top:0;
  border-radius:0 0 16px 16px;box-shadow:${p => p.theme.shadow};
  position:sticky;top:0;z-index:30;
  @media(max-width:900px){position:static;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));}
`;
export const FilterSummary = styled.div`
  strong{display:block;font-size:1.05rem;color:${p => p.theme.text};}
  span{display:block;font-size:0.72rem;color:${p => p.theme.muted};}
`;
export const Field = styled.label`
  display:flex;flex-direction:column;gap:4px;min-width:0;
  span{font-size:0.7rem;font-weight:700;color:${p => p.theme.muted};text-transform:uppercase;letter-spacing:.04em;}
`;
export const Input = styled.input`
  width:100%;height:36px;padding:0 10px;border-radius:9px;font-size:0.85rem;
  border:1px solid ${p => p.theme.line};background:${p => p.theme.input};color:${p => p.theme.text};
  &::placeholder{color:${p => p.theme.faint};}
  &:focus{outline:2px solid ${p => p.theme.accent};outline-offset:1px;}
`;
export const Select = styled.select`
  width:100%;height:36px;padding:0 8px;border-radius:9px;font-size:0.85rem;
  border:1px solid ${p => p.theme.line};background:${p => p.theme.input};color:${p => p.theme.text};
  &:focus{outline:2px solid ${p => p.theme.accent};outline-offset:1px;}
`;
export const Btn = styled.button`
  height:36px;padding:0 14px;border-radius:9px;font-size:0.83rem;font-weight:700;cursor:pointer;
  border:1px solid ${p => p.theme.accent};background:${p => p.theme.accent};
  color:${p => (p.theme.mode === 'dark' ? '#052232' : '#ffffff')};
  &:hover{filter:brightness(1.08);}
  &:disabled{opacity:.45;cursor:not-allowed;}
`;
export const Ghost = styled.button`
  height:36px;padding:0 13px;border-radius:9px;font-size:0.83rem;font-weight:600;cursor:pointer;
  border:1px solid ${p => p.theme.line};background:transparent;color:${p => p.theme.muted};
  &:hover{color:${p => p.theme.text};border-color:${p => p.theme.accent};}
  &:disabled{opacity:.4;cursor:not-allowed;}
`;

// ── Navegación por pestañas ─────────────────────────────────────────────────
export const Tabs = styled.nav`
  margin:16px clamp(14px,3vw,44px) 0;display:flex;gap:4px;overflow-x:auto;
  border-bottom:1px solid ${p => p.theme.line};
`;
export const Tab = styled.button`
  border:0;background:transparent;cursor:pointer;padding:10px 14px;white-space:nowrap;
  font-size:0.88rem;font-weight:700;border-bottom:3px solid transparent;
  color:${p => (p.$active ? p.theme.text : p.theme.muted)};
  border-color:${p => (p.$active ? p.theme.accent : 'transparent')};
  &:hover{color:${p => p.theme.text};}
`;

// ── Encabezados y paneles ───────────────────────────────────────────────────
export const Heading = styled.div`
  display:flex;align-items:flex-end;justify-content:space-between;gap:16px;margin:6px 0 14px;flex-wrap:wrap;
`;
export const Eyebrow = styled.div`
  font-size:0.7rem;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:${p => p.theme.accent};
`;
export const H2 = styled.h2`margin:2px 0 0;font-size:clamp(1.25rem,2.6vw,1.75rem);letter-spacing:-.02em;color:${p => p.theme.text};`;
export const Note = styled.p`margin:0;font-size:0.82rem;color:${p => p.theme.muted};max-width:520px;`;
export const Actions = styled.div`display:flex;gap:8px;flex-wrap:wrap;`;

export const Panel = styled.section`
  background:${p => p.theme.card};border:1px solid ${p => p.theme.line};border-radius:14px;
  box-shadow:${p => p.theme.shadow};overflow:hidden;margin-bottom:14px;
`;
export const PanelHead = styled.div`
  padding:14px 16px 10px;border-bottom:1px solid ${p => p.theme.line2};
  h3{margin:0;font-size:0.98rem;color:${p => p.theme.text};}
  p{margin:4px 0 0;font-size:0.78rem;color:${p => p.theme.muted};}
`;
export const PanelBody = styled.div`padding:15px 16px;`;
export const Grid2 = styled.div`display:grid;grid-template-columns:repeat(auto-fit,minmax(340px,1fr));gap:14px;`;
export const Empty = styled.div`
  padding:16px;border:1px dashed ${p => p.theme.line};border-radius:12px;text-align:center;
  color:${p => p.theme.muted};font-size:0.85rem;
`;

// ── KPIs ────────────────────────────────────────────────────────────────────
export const KpiGrid = styled.div`
  display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:12px;margin-bottom:14px;
`;
export const KpiCard = styled.article`
  min-height:120px;padding:15px;border-radius:13px;display:flex;flex-direction:column;
  background:${p => p.theme.card};border:1px solid ${p => p.theme.line};
  border-left:4px solid ${p => p.$tone || p.theme.accent};box-shadow:${p => p.theme.shadow};
  span{font-size:0.78rem;font-weight:700;color:${p => p.theme.muted};}
  strong{margin-top:8px;font-size:1.9rem;line-height:1;letter-spacing:-.03em;color:${p => p.theme.text};}
  small{margin-top:auto;padding-top:8px;font-size:0.74rem;color:${p => p.theme.faint};}
`;

// ── Barras comparativas (registrados vs certificados) ───────────────────────
export const Bars = styled.div`display:grid;gap:10px;`;
export const BarRow = styled.div`
  display:grid;grid-template-columns:minmax(110px,1fr) 1.6fr auto;gap:10px;align-items:center;
  font-size:0.78rem;color:${p => p.theme.text2};
  @media(max-width:600px){grid-template-columns:100px 1fr auto;}
`;
export const Track = styled.div`
  height:13px;border-radius:99px;background:${p => p.theme.track};position:relative;overflow:hidden;
`;
export const FillA = styled.i`display:block;height:100%;border-radius:99px;background:${p => p.theme.serieA};`;
export const FillB = styled.i`
  position:absolute;left:0;top:0;height:100%;border-radius:99px;background:${p => p.theme.serieB};opacity:.95;
`;
export const BarVal = styled.strong`
  text-align:right;font-size:0.74rem;font-weight:700;color:${p => p.theme.text2};white-space:nowrap;
  em{display:block;font-style:normal;font-weight:400;color:${p => p.theme.muted};}
`;
export const Legend = styled.div`
  display:flex;gap:16px;margin-top:10px;font-size:0.74rem;color:${p => p.theme.muted};flex-wrap:wrap;
  i{display:inline-block;width:11px;height:8px;border-radius:2px;margin-right:5px;}
`;

// ── Tarjetas pequeñas / cubos de journey ────────────────────────────────────
export const MiniGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:12px;`;
export const Mini = styled.article`
  padding:14px;border-radius:12px;border:1px solid ${p => p.theme.line};background:${p => p.theme.card2};
  span{font-size:0.68rem;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:${p => p.theme.accent};}
  strong{display:block;margin-top:6px;font-size:0.95rem;color:${p => p.theme.text};}
  p{margin:3px 0 0;font-size:0.76rem;color:${p => p.theme.muted};}
`;
export const Cubes = styled.div`display:flex;gap:7px;overflow-x:auto;padding-bottom:4px;`;
export const Cube = styled.article`
  min-width:88px;flex:1;padding:10px 8px;border-radius:11px;text-align:center;
  border:1px solid ${p => p.theme.line};
  background:${p => (p.$kind === 'done' ? p.theme.okSoft : p.$kind === 'none' ? p.theme.card2 : p.$kind === 'branch' ? p.theme.branchSoft : p.theme.accentSoft)};
  span{display:block;font-size:0.68rem;color:${p => p.theme.muted};}
  strong{display:block;margin:4px 0;font-size:1.15rem;color:${p => p.theme.text};}
  small{display:block;font-size:0.68rem;color:${p => p.theme.faint};}
`;

// ── Tarjetas seleccionables (módulo / evaluación / requisito) ───────────────
export const CardGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:12px;`;
export const CardBtn = styled.button`
  text-align:left;cursor:pointer;padding:13px;border-radius:12px;font:inherit;transition:.15s;
  background:${p => (p.$active ? p.theme.accentSoft : p.theme.card)};
  border:1px solid ${p => (p.$active ? p.theme.accent : p.theme.line)};
  color:${p => p.theme.text};
  &:hover{border-color:${p => p.theme.accent};}
`;
export const Bar = styled.div`height:9px;border-radius:6px;background:${p => p.theme.track};overflow:hidden;margin-top:7px;`;
export const Fill = styled.div`height:100%;border-radius:6px;`;

// ── Chips ───────────────────────────────────────────────────────────────────
export const Chips = styled.div`display:flex;gap:8px;flex-wrap:wrap;`;
export const Chip = styled.button`
  border-radius:999px;padding:6px 12px;font-size:0.78rem;font-weight:600;cursor:pointer;
  display:inline-flex;align-items:center;gap:6px;
  border:1px solid ${p => (p.$active ? p.theme.accent : p.theme.line)};
  background:${p => (p.$active ? p.theme.accentSoft : p.theme.card)};
  color:${p => (p.$active ? p.theme.text : p.theme.muted)};
  &:hover{color:${p => p.theme.text};border-color:${p => p.theme.accent};}
`;
export const Dot = styled.span`width:9px;height:9px;border-radius:50%;background:${p => p.$c};display:inline-block;`;

// ── Tablas ──────────────────────────────────────────────────────────────────
export const TableWrap = styled.div`
  overflow:auto;max-height:${p => p.$max || '600px'};border:1px solid ${p => p.theme.line};border-radius:12px;
  background:${p => p.theme.card};
`;
export const Table = styled.table`width:100%;border-collapse:collapse;font-size:0.79rem;min-width:640px;`;
export const Th = styled.th`
  position:sticky;top:0;z-index:2;text-align:left;padding:9px 10px;white-space:nowrap;font-weight:700;
  background:${p => p.theme.card2};color:${p => p.theme.muted};border-bottom:1px solid ${p => p.theme.line};
  cursor:${p => (p.$sortable ? 'pointer' : 'default')};
  &:hover{color:${p => (p.$sortable ? p.theme.text : p.theme.muted)};}
`;
export const Td = styled.td`
  padding:8px 10px;white-space:${p => (p.$wrap ? 'normal' : 'nowrap')};
  border-top:1px solid ${p => p.theme.line2};color:${p => p.theme.text2};
`;
export const TableTools = styled.div`
  display:flex;align-items:center;justify-content:space-between;gap:12px;padding:11px 14px;flex-wrap:wrap;
  border-bottom:1px solid ${p => p.theme.line2};font-size:0.8rem;color:${p => p.theme.muted};
`;
export const Badge = styled.span`
  display:inline-block;padding:2px 9px;border-radius:999px;font-size:0.72rem;font-weight:700;
  background:${p => (p.$tone === 'ok' ? p.theme.okSoft : p.$tone === 'warn' ? p.theme.warnSoft : p.$tone === 'bad' ? p.theme.badSoft : p.theme.card2)};
  color:${p => (p.$tone === 'ok' ? p.theme.ok : p.$tone === 'warn' ? p.theme.warn : p.$tone === 'bad' ? p.theme.bad : p.theme.muted)};
  border:1px solid ${p => p.theme.line};
`;
export const Pager = styled.div`
  display:flex;gap:12px;align-items:center;justify-content:center;margin-top:12px;flex-wrap:wrap;
  span{font-size:0.8rem;color:${p => p.theme.muted};}
`;

// ── Encuesta ────────────────────────────────────────────────────────────────
export const EncGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:12px;`;
export const EncCard = styled.article`
  padding:14px 15px;border-radius:13px;background:${p => p.theme.card};border:1px solid ${p => p.theme.line};
  box-shadow:${p => p.theme.shadow};
`;
export const EncQ = styled.div`font-size:0.8rem;font-weight:600;color:${p => p.theme.text2};margin-bottom:8px;min-height:2.4em;`;
export const EncBig = styled.div`font-size:1.75rem;font-weight:800;line-height:1;color:${p => p.$c || p.theme.text};`;
export const EncSub = styled.div`font-size:0.73rem;color:${p => p.theme.muted};margin-top:5px;`;
export const EncOpen = styled.div`
  font-size:0.8rem;color:${p => p.theme.text2};background:${p => p.theme.card2};
  border:1px solid ${p => p.theme.line2};border-radius:9px;padding:8px 10px;margin-top:6px;
  max-height:190px;overflow-y:auto;
  div{padding:4px 0;border-bottom:1px solid ${p => p.theme.line2};}
  div:last-child{border-bottom:0;}
`;

// ── Acceso (gate) ───────────────────────────────────────────────────────────
// ── Satisfacción (modelo: tarjetas por pregunta, aspectos, sentimiento) ─────
export const SurveyGrid = styled.div`
  display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:12px;margin-bottom:14px;
`;
export const SurveyCard = styled.article`
  padding:15px;border-radius:14px;background:${p => p.theme.card};border:1px solid ${p => p.theme.line};
  box-shadow:${p => p.theme.shadow};display:flex;flex-direction:column;
  > span{color:${p => p.theme.accent};font-size:0.7rem;font-weight:800;}
  h3{margin:7px 0;font-size:0.88rem;line-height:1.3;color:${p => p.theme.text2};min-height:2.6em;
     display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;}
  strong{display:block;font-size:1.45rem;line-height:1.15;color:${p => p.theme.text};letter-spacing:-.02em;}
  strong.largo{font-size:1.05rem;}
  small{margin-top:auto;padding-top:6px;color:${p => p.theme.muted};font-size:0.75rem;}
`;
export const AspectGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:12px;`;
export const Aspect = styled.article`
  padding:14px;border:1px solid ${p => p.theme.line};border-radius:12px;background:${p => p.theme.card};
  h4{min-height:3.6em;margin:0 0 8px;font-size:0.86rem;line-height:1.3;color:${p => p.theme.text2};}
  strong{font-size:1.35rem;color:${p => p.theme.text};}
  span,small{display:block;color:${p => p.theme.muted};font-size:0.75rem;}
`;
export const Progress = styled.div`
  height:7px;background:${p => p.theme.track};border-radius:99px;margin:9px 0;overflow:hidden;
  i{display:block;height:100%;background:${p => p.theme.serieB};border-radius:inherit;}
`;
export const Sentiment = styled.div`
  display:grid;grid-template-columns:repeat(3,1fr);gap:10px;
  @media(max-width:600px){grid-template-columns:1fr;}
  button{all:unset;cursor:pointer;padding:16px;border-radius:12px;color:${p => p.theme.text};border:2px solid transparent;}
  button:focus-visible{outline:2px solid ${p => p.theme.accent};outline-offset:2px;}
  button[aria-pressed="true"]{border-color:currentColor;}
  button:nth-child(1){background:${p => p.theme.okSoft};}
  button:nth-child(2){background:${p => p.theme.warnSoft};}
  button:nth-child(3){background:${p => p.theme.badSoft};}
  span{display:block;font-size:0.8rem;color:${p => p.theme.text2};}
  strong{display:block;font-size:1.6rem;margin:2px 0;}
  small{color:${p => p.theme.muted};font-size:0.75rem;}
`;
export const BarBtn = styled(BarRow).attrs({ as: 'button', type: 'button' })`
  all:unset;display:grid;grid-template-columns:minmax(110px,1fr) 1.6fr auto;gap:10px;align-items:center;
  font-size:0.78rem;color:${p => p.theme.text2};cursor:pointer;border-radius:6px;padding:2px 4px;margin:0 -4px;
  background:${p => (p.$on ? p.theme.accentSoft : 'transparent')};
  &:focus-visible{outline:2px solid ${p => p.theme.accent};}
  @media(max-width:600px){grid-template-columns:100px 1fr auto;}
`;
export const Comentario = styled.div`
  padding:10px 0;border-bottom:1px solid ${p => p.theme.line2};font-size:0.84rem;color:${p => p.theme.text2};
  p{margin:0 0 4px;line-height:1.45;}
  small{color:${p => p.theme.muted};font-size:0.73rem;}
`;

// ── Calidad de datos: franja de auditoría ───────────────────────────────────
export const AuditHero = styled.div`
  display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:12px;padding:16px 18px;margin-bottom:14px;
  border-radius:15px;color:${p => p.theme.headText};
  background:linear-gradient(120deg, ${p => p.theme.headFrom}, ${p => p.theme.headTo});
  span{display:block;color:${p => p.theme.headMuted};font-size:0.78rem;}
  strong{font-size:1.6rem;letter-spacing:-.02em;}
  small{display:block;color:${p => p.theme.headMuted};font-size:0.72rem;}
`;

// ── Demografía: mapa de calor día × hora ─────────────────────────────────────
export const Calor = styled.div`
  display: grid;
  grid-template-columns: 76px repeat(24, minmax(18px, 1fr));
  gap: 3px;
  min-width: 620px;
  font-size: 0.68rem;
  color: ${p => p.theme.muted};
  span.dia { align-self: center; font-size: 0.74rem; color: ${p => p.theme.text2}; }
  span.hora { text-align: center; }
  i { display: block; height: 22px; border-radius: 4px; background: ${p => p.theme.track}; position: relative; }
  i b { position: absolute; inset: 0; border-radius: inherit; background: ${p => p.theme.serieA}; }
`;

export const BarrasHora = styled.div`
  display: grid;
  grid-template-columns: repeat(24, minmax(0, 1fr));
  align-items: end;
  gap: 3px;
  height: 150px;
  padding-top: 6px;
  div { display: flex; flex-direction: column; justify-content: flex-end; height: 100%; }
  i { display: block; border-radius: 3px 3px 0 0; background: ${p => p.theme.serieA}; min-height: 2px; }
  i.pico { background: ${p => p.theme.serieB}; }
`;

export const Center = styled.div`
  min-height:100vh;display:flex;align-items:center;justify-content:center;padding:20px;background:${p => p.theme.bg};
`;
export const Gate = styled.div`
  background:${p => p.theme.card};border:1px solid ${p => p.theme.line};border-radius:16px;
  padding:32px 28px;max-width:400px;width:100%;text-align:center;box-shadow:${p => p.theme.shadow};
  h2{margin:0 0 4px;color:${p => p.theme.text};}
`;
export const GateInput = styled(Input)`height:42px;margin:14px 0;font-size:0.95rem;`;
export const Primary = styled(Btn)`width:100%;height:42px;`;
export const Err = styled.div`color:${p => p.theme.bad};font-size:0.83rem;margin-top:10px;`;
export const Foot = styled.footer`
  padding:18px clamp(14px,3vw,44px);font-size:0.74rem;color:${p => p.theme.faint};
  border-top:1px solid ${p => p.theme.line};
`;
